import { useState, useEffect } from 'react';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { UnassignedStation } from '@/api/legacy';

interface OrphanedStationSheetProps {
  station: UnassignedStation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (stationId: string, comment: string) => void;
  isDeleting: boolean;
}

export function OrphanedStationSheet({
  station,
  open,
  onOpenChange,
  onDelete,
  isDeleting,
}: OrphanedStationSheetProps) {
  const [showDelete, setShowDelete] = useState(false);
  const [deleteComment, setDeleteComment] = useState('');

  // Reset state when sheet closes
  useEffect(() => {
    if (!open) {
      setShowDelete(false);
      setDeleteComment('');
    }
  }, [open]);

  if (!station) return null;

  const displayName = station.computername || station.comment || `ID: ${station.id}`;

  const handleDeleteConfirm = () => {
    if (station) {
      onDelete(station.clientPublicKey, deleteComment);
    }
  };

  // Parse log entries (separated by \r\n)
  const logEntries = station.log
    ? station.log.split(/\r?\n/).filter((line) => line.trim())
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <SheetTitle className="text-lg">{displayName}</SheetTitle>
          </div>
          <SheetDescription>
            Потерянная станция - отвязана от клиента
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          {/* Station info */}
          <div className="p-3 rounded-lg bg-muted space-y-2">
            <div className="text-xs text-muted-foreground">ID станции</div>
            <div className="text-sm font-mono break-all">
              {station.clientPublicKey}
            </div>
            {station.comment && (
              <>
                <div className="text-xs text-muted-foreground mt-2">Комментарий</div>
                <div className="text-sm">{station.comment}</div>
              </>
            )}
          </div>

          {/* Log section */}
          {logEntries.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Лог станции</div>
              <div className="p-3 rounded-lg bg-muted max-h-[300px] overflow-y-auto">
                <div className="text-xs space-y-1 font-mono">
                  {logEntries.map((entry, idx) => (
                    <div key={idx} className="break-words">
                      {entry}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Delete section */}
          <div className="pt-4 border-t">
            {!showDelete ? (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => setShowDelete(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Удалить станцию
              </Button>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder="Причина удаления..."
                  value={deleteComment}
                  onChange={(e) => setDeleteComment(e.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setShowDelete(false);
                      setDeleteComment('');
                    }}
                  >
                    Отмена
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="flex-1"
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1" />
                    )}
                    Удалить
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
